import React, { forwardRef, useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { cx } from 'linaria';

import { CarouselItem } from './carousel-item';
import { CarouselDirection } from './carousel.types';
import * as styles from './carousel.styles';

import { VisuallyHidden } from '../';

import { flattenReactChildren } from '../utils/flatten-react-children';
import { mergeRefs } from '../utils/merge-refs';

export interface CarouselProps extends React.AllHTMLAttributes<HTMLElement> {
    /**
     * Orientation flow of the carousel
     * @default row
     */
    direction?: CarouselDirection;
    /**
     * Show indicator controls for the carousel
     * @default false
     */
    indicators?: boolean;
    /**
     * Visual appearance of the carousel - whether it should use dark or light colours
     * @default false
     */
    inverse?: boolean;
    /**
     * Defines a label that describes about the current element.
     */
    'aria-label': string;
}

interface CarouselComposition {
    Item: typeof CarouselItem;
}

type CarouselComponent = React.ForwardRefExoticComponent<
    React.PropsWithChildren<CarouselProps> & React.RefAttributes<HTMLUListElement>
> &
    Partial<CarouselComposition>;

const SCROLL_ROW_PROPERTIES = ['scrollLeft', 'scrollWidth'] as const;
const SCROLL_COLUMN_PROPERTIES = ['scrollTop', 'scrollHeight'] as const;

type ScrollProperties = typeof SCROLL_ROW_PROPERTIES | typeof SCROLL_COLUMN_PROPERTIES;

export const Carousel: CarouselComponent = forwardRef<HTMLUListElement, CarouselProps>((props, ref) => {
    const {
        direction = 'row',
        indicators = false,
        inverse = false,
        'aria-label': ariaLabel,
        className: classNameProp,
        ...passThroughProps
    } = props;

    const [activeIndex, setActiveIndex] = useState(0);

    const carouselRef = useRef<HTMLUListElement>(null);
    const combinedRef = mergeRefs(carouselRef, ref);

    const className = cx(styles.root, styles.directionVariants[direction], classNameProp);
    const flatChildren = flattenReactChildren(props.children);

    console.assert(
        flatChildren.every((child) => typeof child === 'object' && (child as React.ReactElement).type === CarouselItem),
        'Carousel can contain only Carousel.Item as child elements',
    );

    const handleSlideControl = useCallback((index) => {
        const carouselItems = carouselRef.current.children;

        if (!carouselItems) {
            return;
        }

        const currentItem = carouselItems[index];

        currentItem?.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'center',
        });
    }, []);

    const itemLength = flatChildren.length;
    const renderIndicators = useMemo(() => {
        if (!indicators) {
            return null;
        }

        const indicatorElements = [];

        for (let index = 0; index < itemLength; index++) {
            const itemControl = (
                <button
                    key={`slide-${index}`}
                    type="button"
                    className={cx(styles.indicatorButton, inverse && styles.indicatorInverse)}
                    aria-pressed={index === activeIndex}
                    onClick={() => handleSlideControl(index)}
                    data-testid={`carousel-indicator-${index}`}
                >
                    <VisuallyHidden>Slide {index}</VisuallyHidden>
                </button>
            );

            indicatorElements.push(itemControl);
        }

        return <div className={cx(styles.indicatorContainer)}>{indicatorElements}</div>;
    }, [itemLength, indicators, handleSlideControl, activeIndex, inverse]);

    useEffect(() => {
        const carouselElement = carouselRef.current;
        const [scrollDirection, scrollDirectionWidth]: ScrollProperties =
            direction === 'row' ? SCROLL_ROW_PROPERTIES : SCROLL_COLUMN_PROPERTIES;

        function handleScroll() {
            const currentIndex = Math.round(
                (carouselElement[scrollDirection] / carouselElement[scrollDirectionWidth]) * itemLength,
            );

            setActiveIndex(currentIndex);
        }

        carouselElement.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            carouselElement.removeEventListener('scroll', handleScroll);
        };
    }, [itemLength, direction]);

    return (
        <>
            <ul ref={combinedRef} className={className} {...passThroughProps} tabIndex={0} aria-label={ariaLabel}>
                {flatChildren}
            </ul>
            {renderIndicators}
        </>
    );
});

Carousel.displayName = 'Carousel';
Carousel.Item = CarouselItem;
